import styles from './Collapsible.scss';

import cx from 'classnames';
import React from 'react';

export interface IExpanding
  extends React.AllHTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
}

type UiState = {
  isExpanding: boolean;
  height?: string;
  transition?: string;
};

export class Collapsible extends React.PureComponent<IExpanding, UiState> {

  readonly el = React.createRef<HTMLDivElement>();

  constructor(props: IExpanding, context?: any) {
    super(props, context);
    this.state = {
      isExpanding: false,
      height: !props.collapsed ? undefined : `0px`,
    };
  }

  readonly onTransitionEnd = () => {
    if (!this.state.isExpanding) return;
    this.setState({
      height: undefined,
      isExpanding: false,
    });
  };

  expand() {
    const { current } = this.el;
    if (!current) return;

    // get the height of the element's inner content, regardless of its actual size
    const sectionHeight = current.scrollHeight;

    // have the element transition to the height of its inner content
    this.setState({
      height: `${sectionHeight}px`,
      isExpanding: true,
    });

    // safety
    setTimeout(() => this.onTransitionEnd(), 500);
  }

  collapse() {
    const { current } = this.el;
    if (!current) return;

    // get the height of the element's inner content,
    // regardless of its actual size
    const sectionHeight = current.scrollHeight;

    // original transition
    const elementTransition = this.state.transition;

    // temporarily disable all css transitions
    // this is so the user doesn't see us setting the height
    this.setState({
      transition: '',
      isExpanding: false,
    }, () => requestAnimationFrame(() => {
      // once its disabled, force the height to be what
      // its supposed to be
      this.setState({
        height: `${sectionHeight}px`,
        transition: elementTransition,
      }, () => requestAnimationFrame(() => {
        // once that is set, start the transition by
        // setting the height
        this.setState({ height: '0px' });
      }));
    }));
  }

  componentDidMount() {
    if (!this.el.current) return;

    const { style } = this.props;

    this.setState({
      transition: this.el.current.style.transition || (style && style.transition) || undefined,
    });
  }

  componentDidUpdate(prevProps: IExpanding) {
    // not collapsed to collapsed
    if (!prevProps.collapsed && this.props.collapsed) {
      this.collapse();
    }

    // collapsed to not collapsed
    if (!!prevProps.collapsed && !this.props.collapsed) {
      this.expand();
    }
  }

  render() {
    const {
      collapsed,
      children,
      className,
      style,
      ...props
    } = this.props;

    return (
      <div
        ref={this.el}
        className={cx(className, styles.collapsible)}
        aria-hidden={collapsed ? undefined : true}
        onTransitionEnd={this.onTransitionEnd}
        style={{
          ...style,
          height: this.state.height,
          transition: this.state.transition,
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
}
